import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button } from "@tabipla/ui";
import { Smile, Check, AlertTriangle, Camera, Trash2, Loader2, Upload, X } from "lucide-react";
import { useConfirm } from "./useConfirm";

// Avatar color options (Tailwind gradient classes)
const AVATAR_COLORS = [
  "from-cyan-400 to-teal-400",
  "from-blue-400 to-indigo-400",
  "from-purple-400 to-pink-400",
  "from-pink-400 to-rose-400",
  "from-orange-400 to-amber-400",
  "from-emerald-400 to-green-400",
  "from-red-400 to-orange-400",
  "from-violet-400 to-purple-400",
];

// Popular emoji options for avatars
const AVATAR_EMOJIS = [
  "😀",
  "😎",
  "🥳",
  "🤗",
  "😊",
  "🙂",
  "😄",
  "🤩",
  "🐱",
  "🐶",
  "🐰",
  "🦊",
  "🐻",
  "🐼",
  "🐨",
  "🦁",
  "🌸",
  "🌺",
  "🌻",
  "🌷",
  "🌹",
  "🍀",
  "🌈",
  "⭐",
];

export interface AvatarSettingsProps {
  /** Initial avatar emoji */
  initialEmoji?: string | null;
  /** Initial avatar color */
  initialColor?: string | null;
  /** Initial avatar image URL */
  initialImage?: string | null;
  /** User name for fallback display */
  userName?: string;
  /** Whether save is in progress */
  isSaving?: boolean;
  /** Whether save was successful */
  isSuccess?: boolean;
  /** Error message if save failed */
  errorMessage?: string;
  /** Callback when save is clicked */
  onSave: (emoji: string | null, color: string | null) => void;
  /** Callback when clear is clicked */
  onClear: () => void;
  /** Callback when image is uploaded */
  onImageUpload?: (file: File) => Promise<void>;
  /** Callback when image is deleted */
  onImageDelete?: () => Promise<void>;
  /** Whether image upload is in progress */
  isUploadingImage?: boolean;
}

export function AvatarSettings({
  initialEmoji,
  initialColor,
  initialImage,
  userName,
  isSaving = false,
  isSuccess = false,
  errorMessage,
  onSave,
  onClear,
  onImageUpload,
  onImageDelete,
  isUploadingImage = false,
}: AvatarSettingsProps) {
  const { t } = useTranslation();
  const { confirm, ConfirmDialog } = useConfirm();
  const [avatarEmoji, setAvatarEmoji] = useState<string | null>(initialEmoji ?? null);
  const [avatarColor, setAvatarColor] = useState<string | null>(initialColor ?? null);
  const [avatarImage, setAvatarImage] = useState<string | null>(initialImage ?? null);
  // Pending file for preview (not yet uploaded)
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync with initial values when they change
  useEffect(() => {
    setAvatarEmoji(initialEmoji ?? null);
    setAvatarColor(initialColor ?? null);
    setAvatarImage(initialImage ?? null);
    // Clear pending file when initial image changes (e.g., after successful upload)
    setPendingFile(null);
    setPreviewUrl(null);
  }, [initialEmoji, initialColor, initialImage]);

  // Clean up preview URL when component unmounts or preview changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleSave = () => {
    // Save emoji/color settings
    onSave(avatarEmoji, avatarColor);
  };

  const handleClear = () => {
    setAvatarEmoji(null);
    setAvatarColor(null);
    setPendingFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    onClear();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Clean up old preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      // Create preview URL and store pending file
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setPendingFile(file);
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCancelPreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPendingFile(null);
  };

  const handleUploadImage = async () => {
    if (pendingFile && onImageUpload) {
      await onImageUpload(pendingFile);
      setPendingFile(null);
      setPreviewUrl(null);
    }
  };

  const handleImageDelete = async () => {
    if (!onImageDelete) return;

    const confirmed = await confirm({
      title: t("profile.deleteImageTitle", "画像を削除"),
      description: t(
        "profile.deleteImageConfirm",
        "プロフィール画像を削除しますか？削除後は絵文字アバターが表示されます。"
      ),
      confirmText: t("common.delete", "削除"),
      cancelText: t("common.cancel", "キャンセル"),
      variant: "destructive",
    });

    if (confirmed) {
      await onImageDelete();
      setAvatarImage(null);
    }
  };

  // Display image: preview URL takes priority, then saved image
  const displayImage = previewUrl || avatarImage;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center text-lg">
          <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30">
            <Smile className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          </div>
          {t("profile.avatarSettings", "アバター設定")}
        </CardTitle>
        <CardDescription>
          {t(
            "profile.avatarSettingsDescription",
            "グループや精算画面で表示されるアイコンをカスタマイズ"
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Preview - Clickable to upload image */}
        <div className="flex flex-col items-center justify-center gap-3">
          <button
            type="button"
            className="group relative cursor-pointer"
            onClick={() => onImageUpload && !previewUrl && fileInputRef.current?.click()}
            disabled={isUploadingImage || !!previewUrl || !onImageUpload}
            title={
              onImageUpload ? t("profile.clickToChangeImage", "クリックして画像を変更") : undefined
            }
          >
            {displayImage ? (
              <img
                src={displayImage}
                alt="Avatar"
                className={`h-24 w-24 rounded-full object-cover shadow-lg transition-all ${previewUrl ? "ring-2 ring-primary ring-offset-2" : ""} ${onImageUpload && !previewUrl ? "group-hover:brightness-90" : ""}`}
              />
            ) : (
              <div
                className={`flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br ${avatarColor || "from-gray-300 to-gray-400"} text-5xl shadow-lg transition-all ${onImageUpload ? "group-hover:brightness-90" : ""}`}
              >
                {avatarEmoji || userName?.charAt(0).toUpperCase() || "?"}
              </div>
            )}
            {/* Camera overlay on hover */}
            {onImageUpload && !isUploadingImage && !previewUrl && (
              <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 opacity-0 transition-all group-hover:bg-black/30 group-hover:opacity-100">
                <Camera className="h-6 w-6 text-white" />
              </div>
            )}
            {isUploadingImage && (
              <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50">
                <Loader2 className="h-6 w-6 animate-spin text-white" />
              </div>
            )}
          </button>
          {/* Hint for avatar priority */}
          {!previewUrl && !displayImage && (
            <p className="text-center text-xs text-muted-foreground">
              {t("profile.avatarPriorityHint", "画像 > 絵文字 > 名前の頭文字 の順で表示されます")}
            </p>
          )}
          {/* Preview action buttons - shown directly below preview when image is selected */}
          {previewUrl && (
            <div className="flex flex-col items-center gap-2">
              <p className="text-sm font-medium text-primary">
                {t("profile.newImageSelected", "新しい画像が選択されました")}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleUploadImage}
                  disabled={isUploadingImage}
                  className="gap-1"
                >
                  {isUploadingImage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  {t("profile.uploadImage", "アップロード")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancelPreview}
                  disabled={isUploadingImage}
                  className="gap-1"
                >
                  <X className="h-4 w-4" />
                  {t("common.cancel", "キャンセル")}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Image Upload */}
        {onImageUpload && !previewUrl && (
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t("profile.avatarImage", "プロフィール画像")}
            </label>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingImage}
                className="flex-1"
              >
                <Camera className="mr-2 h-4 w-4" />
                {avatarImage
                  ? t("profile.changeImage", "画像を変更")
                  : t("profile.selectImage", "画像を選択")}
              </Button>
              {avatarImage && onImageDelete && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleImageDelete}
                  disabled={isUploadingImage}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("profile.imageHint", "画像がない場合は下の絵文字と背景色が使用されます")}
            </p>
          </div>
        )}

        {/* Emoji Selection */}
        <div className="space-y-2">
          <label className="text-sm font-medium">{t("profile.selectEmoji", "絵文字を選択")}</label>
          <div className="grid grid-cols-8 gap-2">
            {AVATAR_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => setAvatarEmoji(emoji)}
                className={`flex h-10 w-10 items-center justify-center rounded-lg text-xl transition-all hover:bg-muted ${
                  avatarEmoji === emoji ? "ring-2 ring-primary ring-offset-2" : ""
                }`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>

        {/* Color Selection */}
        <div className="space-y-2">
          <label className="text-sm font-medium">{t("profile.selectColor", "背景色を選択")}</label>
          <div className="grid grid-cols-4 gap-2">
            {AVATAR_COLORS.map((color) => (
              <button
                key={color}
                onClick={() => setAvatarColor(color)}
                className={`h-10 rounded-lg bg-gradient-to-br ${color} transition-all hover:scale-105 ${
                  avatarColor === color ? "ring-2 ring-primary ring-offset-2" : ""
                }`}
              />
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            disabled={isSaving || isUploadingImage || !!previewUrl}
            className="flex-1"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("profile.saving", "保存中...")}
              </>
            ) : (
              t("profile.saveAvatar", "アバターを保存")
            )}
          </Button>
          <Button variant="outline" onClick={handleClear} disabled={isSaving || isUploadingImage}>
            {t("profile.clearAvatar", "クリア")}
          </Button>
        </div>
        {isSuccess && (
          <div className="flex items-center gap-2 rounded-xl bg-green-50 p-3 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
            <Check className="h-4 w-4" />
            {t("profile.avatarUpdated", "アバターを更新しました")}
          </div>
        )}
        {errorMessage && (
          <div className="flex items-center gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
            <AlertTriangle className="h-4 w-4" />
            {errorMessage}
          </div>
        )}
      </CardContent>

      <ConfirmDialog />
    </Card>
  );
}
